/*
      Page access


      Handles the 'access' property for pages given a URL path that the page belongs to

 */
import lodash from 'lodash';
import { role } from './index';
import access from './access';

/*
      Shape:
         key is the path and the value is a node
         with properties
            "access"
            "subPath"         path the follows the current "key"

            e.g.,

            for the URL www.abc.com/x/y/z
            {
               x: {
                  env: 'DEVELOPMENT'
                  access: { ... },
                  subPath: {
                     y: {
                        access: {...},
                        subPath: { ... }
                     }
                  }
               }
            }


          Where the access property is
          {
            type: ..,
            value: ...,
          }
 */
const accessPathNodes = {
   'job-feed': {
      access: access.generate.roleAccess(
         {
            partner: [
               role.partner.admin,
               role.partner.employerCentralExternal,
               role.partner.employerCentralInternal,
            ],
         },
      ),
   },
   'syn-job-feed': {
      access: access.generate.roleAccess(
         {
            partner: [
               role.partner.admin,
               role.partner.jobFeedReportExternal,
               role.partner.jobFeedReportInternal,
            ],
         },
      ),
   },
   'syn-sites': {
      access: access.generate.roleAccess(
         {
            partner: [
               role.partner.admin,
               role.partner.syndicatedSitesPartnerExternal,
               role.partner.syndicatedSitesPartnerInternal,
            ],
         },
      ),
   },
   'sponsored-ads': {
      access: access.generate.roleAccess(
         {
            partner: [
               role.partner.admin,
               role.partner.advertiserCentralInternal,
               role.partner.advertiserCentralExternal,
            ],
         },
      ),
      subPath: {
         'edit-advertiser': {
            access: access.getAdminAccessByApp.sponsoredAds
         },
      },
   },
   'syndicated-sponsored-ads': {
      access: access.generate.roleAccess(
         {
            partner: [
               role.partner.admin,
               role.partner.syndicatedPartnerExternal,
               role.partner.syndicatedPartnerInternal,
            ],
         },
      ),
   },
};

// Returns an access object {type, value}
// Given the URL path
const getAccessByURLPath = (urlPath) => {
   if (
      !lodash.isString(urlPath)
      || urlPath === undefined
      || urlPath === null
   ) {
      throw new Error(`Argument must be a String.`);
   }

   let pathAccess;

   const rootPathNode = {
      access: undefined,
      subPath: accessPathNodes,
   };

   let curPathNode = rootPathNode;

   const pathSegmentList = urlPath.split('/').filter((u) => u);
   for (let i = 0; i < pathSegmentList.length; ++i) {
      try {
         const nextPathSegment = pathSegmentList[i];
         const hasNextPathNode = lodash.isObject(curPathNode)
            && lodash.isObject(curPathNode?.subPath)
            && Object.prototype.hasOwnProperty.call(curPathNode.subPath, nextPathSegment);
         if (hasNextPathNode) {
            curPathNode = curPathNode.subPath[nextPathSegment];

            if (curPathNode?.access) {
               pathAccess = curPathNode?.access;
            }
         } else {
            break;
         }
      } catch (e) {
         pathAccess = undefined;
         console.error(e.message);
         break;
      }
   }

   if (pathAccess === undefined) {
      throw new Error(`Unable to find access value for urlPath.`);
   } else {
      return pathAccess;
   }
};

const getAccessByPathList = (pathList) => {
   const pathAccessList = pathList.map((pathItem) => getAccessByURLPath(pathItem));
   let isAllAccessSame = true;

   for (let i = 1; i < pathAccessList.length; ++i) {
      if (JSON.stringify(pathAccessList[0]) !== JSON.stringify(pathAccessList[i])) {
         isAllAccessSame = false;
         break;
      }
   }

   if (!isAllAccessSame) {
      throw new Error(`path list argument does not have the same access.`);
   } else {
      return pathAccessList[0];
   }
};

// Path can be a list of string path or just a stringPath
const getAccessByPath = (path) => {
   if (
      !lodash.isString(path)
      && !(
         lodash.isArray(path)
         && path.every((pathItem) => lodash.isString(pathItem))
      )
   ) {
      throw new Error(`Argument must be a String OR array of string.`);
   }

   return lodash.isString(path) ? getAccessByURLPath(path) : getAccessByPathList(path);
};

export default getAccessByPath;

